import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
    Buildings, CurrencyJpy, TrendUp, Users, Car, ClipboardText,
    MagnifyingGlass, Sparkle, Warning, Image as ImageIcon,
    Bank, Palette, FloppyDisk, ArrowClockwise, Gauge, Receipt,
    PencilSimple, CheckCircle, XCircle,
    Clock, Play, CalendarBlank, ChatCircleText,
} from "@phosphor-icons/react";
import { fmtYmd, fmtYmdTime, fmtYmdSeconds } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";

const TABS = [
    { k: "overview",  label: "概要",         Icon: Gauge },
    { k: "tenants",   label: "テナント管理",  Icon: Buildings },
    { k: "operator",  label: "運営法人設定",  Icon: Palette },
    { k: "banking",   label: "振込先",       Icon: Bank },
    { k: "landing",   label: "フロントCMS",   Icon: ImageIcon },
    { k: "quicklinks",label: "クイックリンクCMS", Icon: ChatCircleText },
    { k: "invoices",  label: "請求管理",      Icon: Receipt },
    { k: "scheduler", label: "スケジューラ",  Icon: Clock },
];

export default function SuperAdmin() {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get("tab") || "overview";
    const [tab, setTab] = useState(urlTab);
    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(false);

    useEffect(() => {
        if (urlTab !== tab) setTab(urlTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTab]);

    const selectTab = (k) => {
        setTab(k);
        setSearchParams({ tab: k });
    };

    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const { data } = await api.get("/admin/operator-settings");
            setSettings(data);
        } catch (e) {
            toast.error("運営設定の取得失敗");
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tab !== "overview" && tab !== "tenants" && tab !== "invoices") {
            loadSettings();
        }
    }, [tab, loadSettings]);

    return (
        <div className="space-y-6" data-testid="super-admin-page">
            <div>
                <p className="prc-label-overline !text-[var(--prc-green)]">// MISSION CONTROL · SAAS OPS</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight text-[var(--prc-ink-2)] mt-1">
                    Super Admin パネル
                </h1>
                <p className="mt-1 text-sm text-[var(--prc-muted)]">運営法人情報、フロントページ、テナント、請求を一元管理。</p>
            </div>

            {/* Tab switcher */}
            <nav className="flex gap-0 border border-[var(--prc-border-strong)] rounded-sm overflow-x-auto" data-testid="admin-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.k}
                        onClick={() => selectTab(t.k)}
                        className={`px-4 py-2.5 text-xs font-mono tracking-wider uppercase flex items-center gap-1.5 transition whitespace-nowrap ${
                            tab === t.k ? "bg-[var(--prc-green)] text-black" : "bg-[var(--prc-surface-2)] text-[var(--prc-muted)] hover:text-[var(--prc-ink-2)]"
                        }`}
                        data-testid={`admin-tab-${t.k}`}
                    >
                        <t.Icon size={16} weight={tab === t.k ? "fill" : "regular"} />
                        {t.label}
                    </button>
                ))}
            </nav>

            {tab === "overview" && <OverviewTab />}
            {tab === "tenants"  && <TenantsTab />}
            {tab === "operator" && <OperatorTab settings={settings} loading={settingsLoading} onSaved={loadSettings} />}
            {tab === "banking"  && <BankingTab settings={settings} loading={settingsLoading} onSaved={loadSettings} />}
            {tab === "landing"  && <LandingCmsTab settings={settings} loading={settingsLoading} onSaved={loadSettings} />}
            {tab === "quicklinks" && <QuickLinksCmsTab settings={settings} loading={settingsLoading} onSaved={loadSettings} />}
            {tab === "invoices" && <InvoicesTab />}
            {tab === "scheduler" && <SchedulerTab />}
        </div>
    );
}

/* ==================== OVERVIEW ==================== */
function OverviewTab() {
    const [metrics, setMetrics] = useState(null);
    const [kpi, setKpi] = useState(null);
    useEffect(() => {
        api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
        api.get("/admin/scheduler/kpi-by-tenant", { params: { days: 30 } })
           .then((r) => setKpi(r.data)).catch(() => {});
    }, []);
    if (!metrics) return <div className="text-[var(--prc-muted)]">読み込み中…</div>;
    const { counts, financials, plan_distribution } = metrics;
    const kpis = [
        { label: "MRR", value: `¥${financials.mrr_jpy.toLocaleString()}`, Icon: CurrencyJpy, color: "var(--prc-green)" },
        { label: "ARR", value: `¥${financials.arr_jpy.toLocaleString()}`, Icon: TrendUp, color: "var(--prc-accent)" },
        { label: "30日解約率", value: `${financials.churn_rate_30d}%`, Icon: Warning, color: financials.churn_rate_30d > 5 ? "var(--prc-red)" : "var(--prc-amber)" },
        { label: "30日売上", value: `¥${(financials.last_30d_revenue_jpy||0).toLocaleString()}`, Icon: Sparkle, color: "var(--prc-green)" },
    ];
    const ct = [
        { label: "テナント", value: counts.companies, Icon: Buildings },
        { label: "ユーザー", value: counts.users, Icon: Users },
        { label: "車両", value: counts.vehicles, Icon: Car },
        { label: "点検", value: counts.inspections, Icon: ClipboardText },
    ];
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-3" data-testid="financial-kpis">
                {kpis.map((k) => (
                    <div key={k.label} className="prc-card p-5 relative">
                        <div className="prc-corner-tag" style={{ borderColor: k.color }} />
                        <div className="flex justify-between items-start">
                            <p className="prc-label-overline">{k.label}</p>
                            <k.Icon size={24} style={{ color: k.color }} />
                        </div>
                        <p className="prc-metric mt-3" style={{ color: k.color }}>{k.value}</p>
                    </div>
                ))}
            </div>
            <div className="grid md:grid-cols-4 gap-3" data-testid="count-kpis">
                {ct.map((k) => (
                    <div key={k.label} className="prc-card p-5">
                        <div className="flex justify-between items-start">
                            <p className="prc-label-overline">{k.label}</p>
                            <k.Icon size={24} className="text-[var(--prc-muted)]" />
                        </div>
                        <p className="prc-metric mt-3">{k.value.toLocaleString()}</p>
                    </div>
                ))}
            </div>
            <div className="prc-card p-5">
                <p className="prc-label-overline">// PLAN DISTRIBUTION</p>
                <div className="flex gap-2 flex-wrap mt-3 font-mono text-sm">
                    {Object.entries(plan_distribution).map(([p, c]) => (
                        <div key={p} className="px-3 py-2 border border-[var(--prc-border-strong)] rounded-sm bg-[var(--prc-surface-2)]">
                            <span className="text-[var(--prc-green)] font-bold uppercase">{p}</span>
                            <span className="text-[var(--prc-muted)] ml-2">{c} 社</span>
                        </div>
                    ))}
                </div>
            </div>

            <ReminderKpiPanel kpi={kpi} />
        </div>
    );
}

/* ---------- Reminder dispatch KPI by tenant (30d) ---------- */
function ReminderKpiPanel({ kpi }) {
    if (!kpi) {
        return (
            <div className="prc-card p-5">
                <p className="prc-label-overline">// REMINDER DISPATCH · 30D</p>
                <p className="text-[var(--prc-muted)] text-sm mt-2">読み込み中…</p>
            </div>
        );
    }
    const { totals, tenants, days } = kpi;
    const top = tenants.slice(0, 10);
    const maxTotal = Math.max(1, ...top.map((t) => t.total));
    return (
        <div className="prc-card p-5" data-testid="reminder-kpi-panel">
            <div className="flex items-center justify-between mb-3">
                <p className="prc-label-overline">// REMINDER DISPATCH · 直近 {days} 日</p>
                <p className="text-[10px] font-mono text-[var(--prc-muted)] uppercase tracking-wider">
                    {totals.tenants} 社が配信中 · {totals.total_recipients} 件の通知
                </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCell label="総配信数" value={totals.total_dispatches} accent="var(--prc-green)" />
                <KpiCell label="車検リマインド" value={totals.shaken} accent="#ffb547" />
                <KpiCell label="NG 通知" value={totals.ng} accent="var(--prc-red)" />
                <KpiCell label="整備予定" value={totals.maintenance} accent="#b45dff" />
            </div>
            <div className="overflow-x-auto">
                <table className="prc-table text-sm" data-testid="reminder-kpi-table">
                    <thead>
                        <tr>
                            <th>テナント</th>
                            <th>プラン</th>
                            <th className="num">稼働車両</th>
                            <th className="num">車検</th>
                            <th className="num">NG</th>
                            <th className="num">整備</th>
                            <th className="num">合計</th>
                            <th className="num">通知数</th>
                            <th>最終配信</th>
                            <th>配信比率</th>
                        </tr>
                    </thead>
                    <tbody>
                        {top.length === 0 && (
                            <tr><td colSpan={10} className="text-center text-[var(--prc-muted)] py-4">配信実績がありません</td></tr>
                        )}
                        {top.map((t) => {
                            const w = Math.round((t.total / maxTotal) * 100);
                            const stale = !t.last_at || (Date.now() - new Date(t.last_at).getTime() > 14 * 86400_000);
                            return (
                                <tr key={t.company_id} data-testid={`kpi-tenant-${t.company_id}`}>
                                    <td className="font-semibold text-[var(--prc-ink-2)]">{t.company_name}</td>
                                    <td><span className="prc-badge info uppercase">{t.plan || "-"}</span></td>
                                    <td className="num">{t.active_vehicles}</td>
                                    <td className="num">{t.shaken}</td>
                                    <td className="num" style={{ color: t.ng > 0 ? "var(--prc-red)" : undefined }}>{t.ng}</td>
                                    <td className="num">{t.maintenance}</td>
                                    <td className="num font-bold">{t.total}</td>
                                    <td className="num">{t.recipients}</td>
                                    <td className="text-xs font-mono">
                                        {t.last_at ? fmtYmd(t.last_at) : "—"}
                                        {stale && t.total > 0 && <span className="prc-badge warn ml-1 text-[9px]">STALE</span>}
                                    </td>
                                    <td>
                                        <div className="h-2 bg-[var(--prc-surface-2)] rounded-sm w-24 overflow-hidden">
                                            <div className="h-full" style={{ width: `${w}%`, background: "var(--prc-green)" }} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {tenants.length > 10 && (
                <p className="text-[10px] text-[var(--prc-muted)] font-mono mt-2">
                    上位 10 社を表示中 · 残り {tenants.length - 10} 社
                </p>
            )}
        </div>
    );
}

function KpiCell({ label, value, accent }) {
    return (
        <div className="border border-[var(--prc-border-strong)] bg-[var(--prc-surface-2)] rounded-sm px-3 py-2">
            <p className="prc-label-overline">{label}</p>
            <p className="text-2xl font-bold font-mono mt-0.5" style={{ color: accent }}>{(value || 0).toLocaleString()}</p>
        </div>
    );
}

/* ==================== TENANTS ==================== */
function TenantsTab() {
    const [tenants, setTenants] = useState([]);
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);

    const load = useCallback(async (search = "") => {
        const { data } = await api.get("/admin/tenants", { params: { q: search || undefined, limit: 100 } });
        setTenants(data.tenants);
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateTenant = async (updates) => {
        try {
            await api.patch(`/admin/tenants/${editing.id}`, updates);
            toast.success("テナントを更新しました");
            setEditing(null);
            load(q);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <MagnifyingGlass size={16} className="text-[var(--prc-muted)]" />
                <input className="prc-input py-1.5 text-sm w-60" placeholder="検索" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} data-testid="tenants-search" />
                <button className="prc-btn-ghost text-xs" onClick={() => load(q)}>検索</button>
                <button className="prc-btn-ghost text-xs ml-auto" onClick={() => load(q)}><ArrowClockwise size={16} /> 更新</button>
            </div>
            <div className="prc-card overflow-hidden">
                <table className="prc-table">
                    <thead>
                        <tr><th>会社</th><th>プラン</th><th>ステータス</th><th className="num">ユーザー</th><th className="num">車両</th><th className="num">点検</th><th>登録日</th><th></th></tr>
                    </thead>
                    <tbody>
                        {tenants.map((t) => (
                            <tr key={t.id} data-testid={`tenant-${t.id}`}>
                                <td><span className="font-semibold text-[var(--prc-ink-2)]">{t.name}</span><div className="text-[10px] text-[var(--prc-tertiary)]">{t.slug}</div></td>
                                <td><span className="prc-badge info uppercase">{t.plan || "-"}</span></td>
                                <td>
                                    {t.subscription_status === "active" && <span className="prc-badge ok">ACTIVE</span>}
                                    {t.subscription_status === "trialing" && <span className="prc-badge warn">TRIAL</span>}
                                    {t.subscription_status === "cancelled" && <span className="prc-badge ng">CANCELLED</span>}
                                    {!t.subscription_status && <span className="prc-badge muted">FREE</span>}
                                </td>
                                <td className="num">{t.user_count}</td>
                                <td className="num">{t.vehicle_count}</td>
                                <td className="num">{t.inspection_count}</td>
                                <td>{fmtYmd(t.created_at, String(t.created_at||"").substring(0,10))}</td>
                                <td className="text-right">
                                    <button className="prc-btn-ghost text-xs" onClick={() => setEditing(t)} data-testid={`edit-tenant-${t.id}`}>
                                        <PencilSimple size={16} /> 編集
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && <TenantEditModal tenant={editing} onClose={() => setEditing(null)} onSave={updateTenant} />}
        </div>
    );
}

function TenantEditModal({ tenant, onClose, onSave }) {
    const [form, setForm] = useState({
        name: tenant.name || "",
        address: tenant.address || "",
        phone: tenant.phone || "",
        plan: tenant.plan || "starter",
        vehicle_limit: tenant.vehicle_limit || 0,
        subscription_status: tenant.subscription_status || "free",
    });
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="tenant-edit-modal">
            <div className="prc-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="prc-label-overline">// EDIT TENANT</p>
                        <h2 className="text-2xl font-bold mt-1 text-[var(--prc-ink-2)]">{tenant.name}</h2>
                    </div>
                    <button className="prc-btn-ghost" onClick={onClose}><XCircle size={16} /></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="mt-5 space-y-4">
                    <Field label="会社名"><input className="prc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="住所"><input className="prc-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                    <Field label="電話"><input className="prc-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="プラン">
                            <select className="prc-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                                <option value="starter">Starter (¥9,800)</option>
                                <option value="business">Business (¥49,800)</option>
                                <option value="enterprise">Enterprise (¥198,000)</option>
                            </select>
                        </Field>
                        <Field label="契約状態">
                            <select className="prc-input" value={form.subscription_status} onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}>
                                <option value="free">free</option>
                                <option value="trialing">trialing</option>
                                <option value="active">active</option>
                                <option value="cancelled">cancelled</option>
                            </select>
                        </Field>
                    </div>
                    <Field label="車両上限（0=無制限）">
                        <input type="number" className="prc-input" value={form.vehicle_limit} onChange={(e) => setForm({ ...form, vehicle_limit: parseInt(e.target.value || "0") })} />
                    </Field>
                    <div className="flex gap-2 justify-end mt-6">
                        <button type="button" className="prc-btn-ghost" onClick={onClose}>キャンセル</button>
                        <button type="submit" className="prc-btn-primary" data-testid="tenant-save-btn"><FloppyDisk size={16} /> 保存</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (<label className="block"><span className="prc-label-overline block mb-1">{label}</span>{children}</label>);
}

/* ==================== OPERATOR ==================== */
function OperatorTab({ settings, loading, onSaved }) {
    const [form, setForm] = useState(null);
    useEffect(() => { if (settings) setForm(settings.operator || {}); }, [settings]);
    if (loading || !form) return <div className="text-[var(--prc-muted)]">読み込み中…</div>;

    const save = async () => {
        try {
            await api.put("/admin/operator-settings", { operator: form });
            toast.success("運営法人情報を保存しました");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };
    return (
        <div className="prc-card p-6 space-y-4" data-testid="operator-tab">
            <p className="prc-label-overline">// OPERATOR ENTITY</p>
            <p className="text-sm text-[var(--prc-muted)]">請求書の発行元情報として使用されます。インボイス制度対応のT番号を正しく登録してください。</p>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="サービス名"><input className="prc-input" value={form.name||""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="op-name" /></Field>
                <Field label="法人名"><input className="prc-input" value={form.legal_name||""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="op-legal-name" /></Field>
                <Field label="代表者"><input className="prc-input" value={form.representative||""} onChange={(e) => setForm({ ...form, representative: e.target.value })} /></Field>
                <Field label="T番号（適格請求書発行事業者登録番号）"><input className="prc-input" value={form.tnumber||""} onChange={(e) => setForm({ ...form, tnumber: e.target.value })} data-testid="op-tnumber" /></Field>
                <Field label="郵便番号"><input className="prc-input" value={form.postal_code||""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="op-postal" placeholder="100-0001" /></Field>
                <Field label="住所"><input className="prc-input" value={form.address||""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                <Field label="電話"><input className="prc-input" value={form.tel||""} onChange={(e) => setForm({ ...form, tel: e.target.value })} /></Field>
                <Field label="FAX"><input className="prc-input" value={form.fax||""} onChange={(e) => setForm({ ...form, fax: e.target.value })} data-testid="op-fax" /></Field>
                <Field label="請求書の送信元メール"><input className="prc-input" value={form.email||""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="サポートメール"><input className="prc-input" value={form.support_email||""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} /></Field>
                <Field label="WhatsApp（番号）"><input className="prc-input" value={form.whatsapp||""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="op-whatsapp" placeholder="+819012345678" /></Field>
                <Field label="LINE ID"><input className="prc-input" value={form.line_id||""} onChange={(e) => setForm({ ...form, line_id: e.target.value })} data-testid="op-lineid" /></Field>
                <Field label="Webサイト"><input className="prc-input" value={form.website||""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
                <Field label="営業時間"><input className="prc-input" value={form.business_hours||""} onChange={(e) => setForm({ ...form, business_hours: e.target.value })} data-testid="op-hours" placeholder="平日 09:00 - 18:00（土日祝 休業）" /></Field>
            </div>
            <div className="flex justify-end"><button className="prc-btn-primary" onClick={save} data-testid="op-save"><FloppyDisk size={16} /> 保存</button></div>
        </div>
    );
}

/* ==================== QUICK LINKS CMS ==================== */
function QuickLinksCmsTab({ settings, loading, onSaved }) {
    const [cms, setCms] = useState(null);
    useEffect(() => { if (settings) setCms(settings.cms || {}); }, [settings]);
    if (loading || !cms) return <div className="text-[var(--prc-muted)]">読み込み中…</div>;

    const news = cms.news || [];
    const setField = (k, v) => setCms({ ...cms, [k]: v });
    const addNews = () => setField("news", [{ date: new Date().toISOString().slice(0, 10), title: "" }, ...news]);
    const updateNews = (i, patch) => setField("news", news.map((n, idx) => idx === i ? { ...n, ...patch } : n));
    const removeNews = (i) => setField("news", news.filter((_, idx) => idx !== i));

    const save = async () => {
        try {
            await api.put("/admin/operator-settings", { cms });
            toast.success("クイックリンクCMSを保存しました");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="quicklinks-cms-tab">
            <div className="prc-card p-6 space-y-4">
                <p className="prc-label-overline">// QUICK LINKS CMS</p>
                <p className="text-sm text-[var(--prc-muted)]">
                    ランディング下部・ログイン画面・サイドバーから開くクイックリンクの各ポップアップ内容を編集します。
                    連絡先・営業時間は「運営法人設定」タブから編集してください。
                </p>
            </div>

            <TextareaBlock label="企業理念 (philosophy)" value={cms.philosophy} onChange={(v) => setField("philosophy", v)} testid="cms-philosophy" />
            <TextareaBlock label="代表挨拶 (greeting)" value={cms.greeting} onChange={(v) => setField("greeting", v)} testid="cms-greeting" />
            <TextareaBlock label="運営会社について (about)" value={cms.about} onChange={(v) => setField("about", v)} testid="cms-about" />
            <TextareaBlock label="採用情報 (careers)" value={cms.careers} onChange={(v) => setField("careers", v)} testid="cms-careers" />
            <TextareaBlock label="会員募集 (members)" value={cms.members} onChange={(v) => setField("members", v)} testid="cms-members" />
            <TextareaBlock label="個人情報取扱 (privacy_policy)" value={cms.privacy_policy} onChange={(v) => setField("privacy_policy", v)} testid="cms-privacy" />
            <TextareaBlock label="特定商取引法 (tokushoho)" value={cms.tokushoho} onChange={(v) => setField("tokushoho", v)} testid="cms-tokushoho" />

            <div className="prc-card p-6 space-y-3" data-testid="cms-news-editor">
                <div className="flex items-center justify-between">
                    <p className="prc-label-overline">// お知らせ (news)</p>
                    <button onClick={addNews} className="prc-btn-ghost" data-testid="cms-news-add">+ 追加</button>
                </div>
                {news.length === 0 && <p className="text-sm text-[var(--prc-muted)]">お知らせがありません</p>}
                <div className="space-y-2">
                    {news.map((n, i) => (
                        <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2 items-start" data-testid={`cms-news-row-${i}`}>
                            <DatePickerYmd value={n.date || ""} onChange={(val) => updateNews(i, { date: val })} />
                            <input className="prc-input" value={n.title || ""} onChange={(e) => updateNews(i, { title: e.target.value })} placeholder="タイトル" />
                            <button onClick={() => removeNews(i)} className="prc-btn-ghost text-[var(--prc-red)] !px-2" aria-label="delete">×</button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <button className="prc-btn-primary inline-flex items-center gap-1.5" onClick={save} data-testid="cms-save">
                    <FloppyDisk size={16} /> 保存
                </button>
            </div>
        </div>
    );
}

function TextareaBlock({ label, value, onChange, testid }) {
    return (
        <div className="prc-card p-6 space-y-2">
            <p className="prc-label-overline">// {label}</p>
            <textarea
                className="prc-input w-full min-h-[120px] font-sans"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder="本文を入力（改行可）"
            />
        </div>
    );
}


/* ==================== BANKING ==================== */
function BankingTab({ settings, loading, onSaved }) {
    const [form, setForm] = useState(null);
    useEffect(() => { if (settings) setForm(settings.banking || {}); }, [settings]);
    if (loading || !form) return <div className="text-[var(--prc-muted)]">読み込み中…</div>;
    const save = async () => {
        try {
            await api.put("/admin/operator-settings", { banking: form });
            toast.success("振込先を保存しました");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
        <div className="prc-card p-6 space-y-4" data-testid="banking-tab">
            <p className="prc-label-overline">// BANK ACCOUNT</p>
            <p className="text-sm text-[var(--prc-muted)]">銀行振込の場合の入金先。請求書 PDF に表示されます（将来機能）。</p>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="銀行名"><input className="prc-input" value={form.bank_name||""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bk-bank" /></Field>
                <Field label="支店"><input className="prc-input" value={form.branch||""} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></Field>
                <Field label="種別">
                    <select className="prc-input" value={form.account_type||"普通"} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                        <option>普通</option><option>当座</option><option>貯蓄</option>
                    </select>
                </Field>
                <Field label="口座番号"><input className="prc-input" value={form.account_number||""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="bk-number" /></Field>
                <Field label="口座名義"><input className="prc-input" value={form.account_holder||""} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} /></Field>
                <Field label="SWIFT (任意)"><input className="prc-input" value={form.swift_code||""} onChange={(e) => setForm({ ...form, swift_code: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end"><button className="prc-btn-primary" onClick={save} data-testid="bk-save"><FloppyDisk size={16} /> 保存</button></div>
        </div>
    );
}

/* ==================== LANDING CMS ==================== */
function LandingCmsTab({ settings, loading, onSaved }) {
    const [branding, setBranding] = useState(null);
    const [landing, setLanding] = useState(null);
    useEffect(() => {
        if (settings) {
            setBranding(settings.branding || {});
            setLanding(settings.landing || {});
        }
    }, [settings]);
    if (loading || !branding || !landing) return <div className="text-[var(--prc-muted)]">読み込み中…</div>;

    const saveAll = async () => {
        try {
            await api.put("/admin/operator-settings", { branding, landing });
            toast.success("フロントページを更新しました。ブラウザリロードで反映されます。");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const handleLogoUpload = (field) => async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 2_000_000) { toast.error("画像は 2MB 以下にしてください"); return; }
        const reader = new FileReader();
        reader.onload = () => setBranding({ ...branding, [field]: reader.result });
        reader.readAsDataURL(f);
    };

    const updateStat = (i, key, val) => {
        const stats = [...(landing.stats || [])];
        stats[i] = { ...stats[i], [key]: val };
        setLanding({ ...landing, stats });
    };
    const addStat = () => setLanding({ ...landing, stats: [...(landing.stats||[]), { value: "", label: "" }] });
    const removeStat = (i) => setLanding({ ...landing, stats: (landing.stats||[]).filter((_, j) => j !== i) });

    const updateFeature = (i, key, val) => {
        const features = [...(landing.features || [])];
        features[i] = { ...features[i], [key]: val };
        setLanding({ ...landing, features });
    };
    const addFeature = () => setLanding({ ...landing, features: [...(landing.features||[]), { title: "", desc: "" }] });
    const removeFeature = (i) => setLanding({ ...landing, features: (landing.features||[]).filter((_, j) => j !== i) });

    return (
        <div className="space-y-4" data-testid="landing-cms-tab">
            {/* Branding */}
            <div className="prc-card p-6 space-y-4">
                <p className="prc-label-overline">// BRANDING</p>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <span className="prc-label-overline block mb-1">ロゴ</span>
                        <div className="border border-dashed border-[var(--prc-border-strong)] rounded-sm p-4 bg-[var(--prc-surface-2)] flex flex-col items-center gap-2 min-h-[140px] justify-center">
                            {branding.logo_data_url ? (
                                <img src={branding.logo_data_url} alt="logo" className="max-h-20" />
                            ) : (
                                <p className="text-xs text-[var(--prc-muted)]">未登録</p>
                            )}
                            <input type="file" accept="image/*" onChange={handleLogoUpload("logo_data_url")} className="text-xs w-full" data-testid="logo-upload" />
                            {branding.logo_data_url && (
                                <button className="prc-btn-ghost text-[10px] mt-1" onClick={() => setBranding({ ...branding, logo_data_url: null })}>削除</button>
                            )}
                        </div>
                    </div>
                    <div>
                        <Field label="ブランド名"><input className="prc-input" value={branding.brand_name||""} onChange={(e) => setBranding({ ...branding, brand_name: e.target.value })} data-testid="brand-name" /></Field>
                        <div className="mt-3"><Field label="ブランドサブ"><input className="prc-input" value={branding.brand_sub||""} onChange={(e) => setBranding({ ...branding, brand_sub: e.target.value })} /></Field></div>
                    </div>
                    <Field label="キャッチコピー"><textarea rows={4} className="prc-input" value={branding.brand_tagline||""} onChange={(e) => setBranding({ ...branding, brand_tagline: e.target.value })} data-testid="brand-tagline" /></Field>
                </div>
                <Field label="ヒーロー背景画像 URL (またはデータURL)"><input className="prc-input" value={branding.hero_image_url||""} onChange={(e) => setBranding({ ...branding, hero_image_url: e.target.value })} data-testid="hero-image-url" /></Field>
                {branding.hero_image_url && (
                    <div className="relative h-32 bg-cover bg-center rounded-sm border border-[var(--prc-border)]" style={{ backgroundImage: `url(${branding.hero_image_url})` }} />
                )}
            </div>

            {/* Landing copy */}
            <div className="prc-card p-6 space-y-4">
                <p className="prc-label-overline">// HERO COPY</p>
                <div className="grid md:grid-cols-3 gap-3">
                    <Field label="タイトル 1 行目"><input className="prc-input" value={landing.hero_title_1||""} onChange={(e) => setLanding({ ...landing, hero_title_1: e.target.value })} data-testid="hero-title-1" /></Field>
                    <Field label="タイトル 2 行目（緑強調）"><input className="prc-input" value={landing.hero_title_2||""} onChange={(e) => setLanding({ ...landing, hero_title_2: e.target.value })} data-testid="hero-title-2" /></Field>
                    <Field label="タイトル 3 行目"><input className="prc-input" value={landing.hero_title_3||""} onChange={(e) => setLanding({ ...landing, hero_title_3: e.target.value })} data-testid="hero-title-3" /></Field>
                </div>
                <Field label="説明文 (sub)"><textarea rows={3} className="prc-input" value={landing.hero_sub||""} onChange={(e) => setLanding({ ...landing, hero_sub: e.target.value })} data-testid="hero-sub" /></Field>
                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="CTA プライマリ"><input className="prc-input" value={landing.cta_primary||""} onChange={(e) => setLanding({ ...landing, cta_primary: e.target.value })} /></Field>
                    <Field label="CTA セカンダリ"><input className="prc-input" value={landing.cta_secondary||""} onChange={(e) => setLanding({ ...landing, cta_secondary: e.target.value })} /></Field>
                </div>
            </div>

            {/* Stats */}
            <div className="prc-card p-6 space-y-3">
                <div className="flex justify-between items-center"><p className="prc-label-overline">// STATS</p>
                    <button className="prc-btn-ghost text-xs" onClick={addStat}>+ 追加</button></div>
                {(landing.stats||[]).map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <input className="prc-input w-32" placeholder="値" value={s.value} onChange={(e) => updateStat(i, "value", e.target.value)} />
                        <input className="prc-input flex-1" placeholder="ラベル" value={s.label} onChange={(e) => updateStat(i, "label", e.target.value)} />
                        <button className="prc-btn-ghost text-[var(--prc-red)]" onClick={() => removeStat(i)}><XCircle size={16} /></button>
                    </div>
                ))}
            </div>

            {/* Features */}
            <div className="prc-card p-6 space-y-3">
                <div className="flex justify-between items-center"><p className="prc-label-overline">// FEATURES</p>
                    <button className="prc-btn-ghost text-xs" onClick={addFeature}>+ 追加</button></div>
                {(landing.features||[]).map((f, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <input className="prc-input w-48" placeholder="タイトル" value={f.title} onChange={(e) => updateFeature(i, "title", e.target.value)} />
                        <input className="prc-input flex-1" placeholder="説明" value={f.desc} onChange={(e) => updateFeature(i, "desc", e.target.value)} />
                        <button className="prc-btn-ghost text-[var(--prc-red)]" onClick={() => removeFeature(i)}><XCircle size={16} /></button>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="prc-card p-6">
                <Field label="フッター注釈"><textarea rows={2} className="prc-input" value={landing.footer_note||""} onChange={(e) => setLanding({ ...landing, footer_note: e.target.value })} /></Field>
            </div>

            <div className="flex justify-end sticky bottom-4">
                <button className="prc-btn-primary shadow-lg" onClick={saveAll} data-testid="landing-save"><FloppyDisk size={16} /> すべて保存</button>
            </div>
        </div>
    );
}

/* ==================== INVOICES ==================== */
function InvoicesTab() {
    const [tenants, setTenants] = useState([]);
    const [selected, setSelected] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [issuing, setIssuing] = useState(false);
    const [form, setForm] = useState({ plan: "business", amount: "", mark_paid: false, period_start: "", period_end: "", notes: "" });

    useEffect(() => {
        api.get("/admin/tenants").then((r) => setTenants(r.data.tenants));
    }, []);

    const loadInvoices = async (t) => {
        setSelected(t);
        const { data } = await api.get(`/admin/tenants/${t.id}/invoices`);
        setInvoices(data.invoices);
    };

    const issueInvoice = async (e) => {
        e.preventDefault();
        setIssuing(true);
        try {
            const payload = { plan: form.plan, mark_paid: form.mark_paid };
            if (form.amount !== "") payload.amount = parseFloat(form.amount);
            if (form.period_start) payload.period_start = form.period_start;
            if (form.period_end) payload.period_end = form.period_end;
            if (form.notes) payload.notes = form.notes;
            await api.post(`/admin/tenants/${selected.id}/invoice`, payload);
            toast.success("請求書を発行しました");
            loadInvoices(selected);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setIssuing(false);
        }
    };

    const downloadPdf = async (inv) => {
        try {
            const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
            window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
        } catch (e) { toast.error("PDF取得失敗"); }
    };

    return (
        <div className="grid md:grid-cols-[260px_1fr] gap-4" data-testid="invoices-tab">
            <div className="prc-card p-3 max-h-[70vh] overflow-y-auto">
                <p className="prc-label-overline mb-2">// TENANTS</p>
                {tenants.map((t) => (
                    <button key={t.id} onClick={() => loadInvoices(t)}
                        className={`w-full text-left p-2 rounded-sm text-sm mb-1 transition ${selected?.id === t.id ? "bg-[rgba(0,255,156,0.1)] border border-[var(--prc-green)]" : "hover:bg-[var(--prc-surface-2)]"}`}
                        data-testid={`inv-tenant-${t.id}`}>
                        <div className="font-semibold text-[var(--prc-ink-2)]">{t.name}</div>
                        <div className="text-[10px] font-mono text-[var(--prc-muted)] uppercase">{t.plan}</div>
                    </button>
                ))}
            </div>
            <div className="space-y-4">
                {!selected ? (
                    <div className="prc-card p-8 text-center text-[var(--prc-muted)]">左のリストからテナントを選択</div>
                ) : (
                    <>
                        <div className="prc-card p-5">
                            <p className="prc-label-overline">// ISSUE NEW INVOICE</p>
                            <h3 className="text-lg font-bold text-[var(--prc-ink-2)] mt-1">{selected.name}</h3>
                            <form onSubmit={issueInvoice} className="mt-4 grid md:grid-cols-3 gap-3">
                                <Field label="プラン">
                                    <select className="prc-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                                        <option value="starter">Starter</option>
                                        <option value="business">Business</option>
                                        <option value="enterprise">Enterprise</option>
                                    </select>
                                </Field>
                                <Field label="金額（空欄でプラン標準）"><input className="prc-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="49800" data-testid="inv-amount" /></Field>
                                <Field label="支払い状態">
                                    <select className="prc-input" value={form.mark_paid ? "paid" : "unpaid"} onChange={(e) => setForm({ ...form, mark_paid: e.target.value === "paid" })}>
                                        <option value="unpaid">未払い</option>
                                        <option value="paid">支払済</option>
                                    </select>
                                </Field>
                                <Field label="期間開始"><DatePickerYmd value={form.period_start} onChange={(val) => setForm({ ...form, period_start: val })} /></Field>
                                <Field label="期間終了"><DatePickerYmd value={form.period_end} onChange={(val) => setForm({ ...form, period_end: val })} /></Field>
                                <Field label="備考"><input className="prc-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                                <div className="md:col-span-3 flex justify-end">
                                    <button className="prc-btn-primary" disabled={issuing} data-testid="inv-issue-btn"><Receipt size={16} /> {issuing ? "発行中…" : "請求書を発行"}</button>
                                </div>
                            </form>
                        </div>
                        <div className="prc-card overflow-hidden">
                            <table className="prc-table">
                                <thead><tr><th>番号</th><th>日付</th><th>プラン</th><th className="num">合計</th><th>状態</th><th></th></tr></thead>
                                <tbody>
                                    {invoices.length === 0 && <tr><td colSpan="6" className="text-center p-4 text-[var(--prc-muted)]">請求書なし</td></tr>}
                                    {invoices.map((inv) => (
                                        <tr key={inv.id}>
                                            <td className="text-[var(--prc-accent)]">{inv.invoice_number}</td>
                                            <td>{fmtYmd(inv.issued_at, String(inv.issued_at).substring(0, 10))}</td>
                                            <td>{inv.plan}{inv.manual && <span className="prc-badge muted ml-1 text-[9px]">手動</span>}</td>
                                            <td className="num font-bold">¥{(inv.total_amount||0).toLocaleString()}</td>
                                            <td>{inv.status === "paid" ? <span className="prc-badge ok">支払済</span> : <span className="prc-badge ng">未払い</span>}</td>
                                            <td className="text-right"><button className="prc-btn-ghost text-xs" onClick={() => downloadPdf(inv)}><CheckCircle size={16} /> PDF</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ==================== SCHEDULER ==================== */

function SchedulerTab() {
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState({ runs: [], recent_reminders: [] });
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l] = await Promise.all([
                api.get("/admin/scheduler/status"),
                api.get("/admin/scheduler/logs", { params: { limit: 50 } }),
            ]);
            setStatus(s.data);
            setLogs(l.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "スケジューラ情報取得失敗");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runNow = async () => {
        if (!window.confirm("今すぐリマインド配信を実行しますか？")) return;
        setRunning(true);
        try {
            const { data } = await api.post("/admin/scheduler/run-now");
            toast.success(`配信完了: 車検${data.stats.shaken_sent} / NG${data.stats.ng_sent} / 整備${data.stats.maint_sent}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "実行失敗");
        } finally {
            setRunning(false);
        }
    };

    const state = status?.state || {};
    const lastRun = status?.last_run || {};
    const nextJob = state.jobs?.[0];

    return (
        <div className="space-y-5" data-testid="scheduler-tab">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="prc-card p-5">
                    <p className="prc-label-overline">SCHEDULER</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: state.running ? "var(--prc-green)" : "var(--prc-red)" }} data-testid="sched-state">
                        {state.running ? "RUNNING" : "STOPPED"}
                    </p>
                    <p className="text-xs text-[var(--prc-muted)] mt-1 font-mono">
                        {nextJob ? `NEXT: ${fmtYmdSeconds(nextJob.next_run_time, new Date(nextJob.next_run_time).toLocaleString("ja-JP"))}` : "no jobs"}
                    </p>
                </div>
                <div className="prc-card p-5">
                    <p className="prc-label-overline">LAST RUN</p>
                    <p className="text-sm font-mono mt-1 text-[var(--prc-ink-2)]">
                        {lastRun.finished_at ? fmtYmdSeconds(lastRun.finished_at, new Date(lastRun.finished_at).toLocaleString("ja-JP")) : "—"}
                    </p>
                    <p className="text-xs text-[var(--prc-muted)] mt-1">
                        {lastRun.stats ? `車検 ${lastRun.stats.shaken_sent} / NG ${lastRun.stats.ng_sent} / 整備 ${lastRun.stats.maint_sent}` : "未実行"}
                    </p>
                    {lastRun.error && <p className="text-xs text-[var(--prc-red)] mt-1">ERR: {lastRun.error}</p>}
                </div>
                <div className="prc-card p-5 flex flex-col justify-between">
                    <div>
                        <p className="prc-label-overline">MANUAL TRIGGER</p>
                        <p className="text-xs text-[var(--prc-muted)] mt-1">テスト配信や緊急時にご利用ください</p>
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={runNow} disabled={running} className="prc-btn-primary inline-flex items-center gap-1.5" data-testid="sched-run-now">
                            <Play size={16} weight="fill" />
                            {running ? "実行中…" : "今すぐ実行"}
                        </button>
                        <button onClick={load} disabled={loading} className="prc-btn-ghost inline-flex items-center gap-1.5" data-testid="sched-refresh">
                            <ArrowClockwise size={16} /> 更新
                        </button>
                    </div>
                </div>
            </div>

            {/* Run history */}
            <div className="prc-card p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Clock size={24} className="text-[var(--prc-green)]" weight="fill" />
                    <h3 className="font-bold text-[var(--prc-ink-2)]">実行履歴</h3>
                </div>
                {logs.runs.length === 0 ? (
                    <p className="text-sm text-[var(--prc-muted)]">履歴がありません。</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="prc-table text-sm">
                            <thead>
                                <tr>
                                    <th>開始日時</th>
                                    <th>トリガー</th>
                                    <th className="num">車検</th>
                                    <th className="num">NG</th>
                                    <th className="num">整備</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.runs.map((r) => (
                                    <tr key={r.id} data-testid={`sched-run-${r.id}`}>
                                        <td className="font-mono text-xs">{fmtYmdSeconds(r.started_at, new Date(r.started_at).toLocaleString("ja-JP"))}</td>
                                        <td className="text-xs">{r.triggered_by}</td>
                                        <td className="num">{r.stats?.shaken_sent || 0}</td>
                                        <td className="num">{r.stats?.ng_sent || 0}</td>
                                        <td className="num">{r.stats?.maint_sent || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Recent reminders */}
            <div className="prc-card p-5">
                <div className="flex items-center gap-2 mb-3">
                    <CalendarBlank size={24} className="text-[var(--prc-green)]" weight="fill" />
                    <h3 className="font-bold text-[var(--prc-ink-2)]">直近の配信ログ</h3>
                </div>
                {logs.recent_reminders.length === 0 ? (
                    <p className="text-sm text-[var(--prc-muted)]">配信がまだありません。</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="prc-table text-sm">
                            <thead>
                                <tr>
                                    <th>配信日</th>
                                    <th>種別</th>
                                    <th>対象</th>
                                    <th className="num">受信者</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.recent_reminders.map((r) => (
                                    <tr key={r.id}>
                                        <td className="font-mono text-xs">{r.trigger_date}</td>
                                        <td><span className="prc-badge">{r.kind}</span></td>
                                        <td className="text-xs">{r.meta?.plate_number || r.vehicle_id}</td>
                                        <td className="num">{r.meta?.recipients || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

